import {
  Card,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Row,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { isBefore2025 as isBefore2025Func } from '@/features/project/_utils/project';
import {
  ExistenceOrAbsenceLabelModifier,
  FundAdditionalAttributesModel,
  FundNecessityTypeLabelModifier,
  type ProjectModel,
} from '@/features/project/projects/_models/project';
import { FALLBACK_TEXT } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = z.infer<typeof ProjectModel>;

export const FundAdditionalAttributesLocalGovernment: FC<Props> = (project) => {
  const { t } = useTranslation();

  const parsed = FundAdditionalAttributesModel.safeParse(
    project.fundAdditionalAttributes,
  );

  const isBefore2025 = useMemo(() => isBefore2025Func(project), [project]);

  if (!parsed.success) {
    return null;
  }

  return (
    <Row gutter={[0, 24]}>
      {isBefore2025 && (
        <ContentWrapper title={t('necessity-of-fund-method')}>
          <ContentItem borderBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="fund-necessity-type"
                  title={t('fund-necessity-type')}
                  text={
                    parsed.data.necessityType &&
                    FundNecessityTypeLabelModifier[parsed.data.necessityType]
                  }
                />,
                <TitledText
                  key="fund-necessity-reasons-for-local-government"
                  title={t('fund-necessity-reasons-for-local-government')}
                  text={parsed.data.necessityReasons}
                />,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      )}

      <ContentWrapper
        title={
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType: 'KS_LOCAL_GOVERNMENT',
              ksLocalGovernment: {
                field: 'ks.local-government.composition-backgrounds-title',
                path: { project: project.id },
              },
            })}
          >
            <Text variant="h3" text={t('composition-backgrounds')} />
          </PinpointMarker>
        }
        borderBottom={isBefore2025 ? undefined : 0}
      >
        <ContentItem borderBottom={0}>
          {parsed.data.compositionBackgrounds?.length ? (
            <Row gutter={[24, 24]}>
              {parsed.data.compositionBackgrounds.map((item, index) => {
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  <Col span={12} key={index}>
                    <Card>
                      <Row gutter={[24, 24]}>
                        {isBefore2025 && (
                          <Col span={12}>
                            <TitledText
                              title={t('fiscal-year-of-budgetary-provision')}
                              text={item.fiscalYearOfBudgetaryProvision}
                            />
                          </Col>
                        )}
                        <Col span={12}>
                          <TitledText
                            title={
                              index > 0
                                ? t('added-fiscal-year')
                                : t('fiscal-year-of-creation')
                            }
                            text={item.fiscalYearOfCreation}
                          />
                        </Col>
                        <Col span={12}>
                          <TitledText
                            title={t(
                              'have-applied-law-of-appropriateness-of-subsidies',
                            )}
                            text={
                              typeof item.haveAppliedLawOfAppropriatenessOfSubsidies ===
                              'boolean'
                                ? ExistenceOrAbsenceLabelModifier[
                                    item.haveAppliedLawOfAppropriatenessOfSubsidies.toString()
                                  ]
                                : item.haveAppliedLawOfAppropriatenessOfSubsidies
                            }
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            FALLBACK_TEXT
          )}
        </ContentItem>
      </ContentWrapper>

      {isBefore2025 && (
        <ContentWrapper title={t('scheduled-end-time')} borderBottom={0}>
          <ContentItem borderBottom={0}>
            <ContentItems
              items={[
                <TitledText
                  key="fund-scheduled-end-period-for-local-government"
                  title={t('fund-scheduled-end-period-for-local-government')}
                  text={parsed.data.scheduledEnd?.endDate}
                />,
                <TitledText
                  key="end-date-of-new-application-for-local-government"
                  title={t('end-date-of-new-application-for-local-government')}
                  text={
                    parsed.data.scheduledEnd?.endDateOfNewApplicationReceiving
                  }
                />,
              ]}
            />
          </ContentItem>
        </ContentWrapper>
      )}
    </Row>
  );
};
